import { PartyType, useAddLegalEntityToRegisterMutation } from 'generated/gql';
import { t } from '@lingui/macro';
import { SidebarFormContainer, SidebarFormContent } from 'components/SidebarForm/SidebarForm';
import type { PartyCreateFormValues } from 'forms/mutation/PartyCreateForm';
import { PartyCreateForm, partyCreateFormSchema } from 'forms/mutation/PartyCreateForm';
import type { Register } from 'generated/gql';
import { useGlobalError } from 'hooks/useGlobalError';
import React from 'react';
import { useCreateLegalEntity, useCreateNaturalPerson } from 'wizardsteps/helpers/useCreateParty';

interface PartyCreateSidebarFormProps {
  register: Register;
  isSidebarShown: boolean;
  onSidebarClose: () => void;
  onPartyCreate: (partyId: string) => void;
  fixedPartyType?: PartyType;
}
export const PartyCreateSidebarForm = ({
  isSidebarShown,
  ...props
}: PartyCreateSidebarFormProps) => {
  return (
    <SidebarFormContainer isDrawerShown={isSidebarShown} width={720}>
      <PartyCreateSidebarFormContent {...props} />
    </SidebarFormContainer>
  );
};

const PartyCreateSidebarFormContent = ({
  register,
  onSidebarClose,
  onPartyCreate,
  fixedPartyType,
}: Omit<PartyCreateSidebarFormProps, 'isSidebarShown'>) => {
  const [, setGlobalError] = useGlobalError();
  const createLegalEntity = useCreateLegalEntity(register.id);
  const createNaturalPerson = useCreateNaturalPerson(register.id);
  const [addLegalEntityToRegister] = useAddLegalEntityToRegisterMutation();

  const handlePartyCreate = async (partyCreateValues: PartyCreateFormValues) => {
    if (partyCreateValues.legalEntity?.id) {
      await addLegalEntityToRegister({
        variables: {
          legalEntityId: partyCreateValues.legalEntity.id,
          registerId: register.id,
        },
        onCompleted: ({ addLegalEntityToRegister }) => {
          onPartyCreate(addLegalEntityToRegister.id);
          onSidebarClose();
        },
      });
      return;
    }
    const createParty =
      partyCreateValues.partyType === PartyType.LegalEntity
        ? createLegalEntity
        : createNaturalPerson;

    const response = await createParty(partyCreateValues);
    if (!response.id) return setGlobalError(response.message);
    onPartyCreate(response.id);
    onSidebarClose();
  };

  const hasAdministerRights = register.entitlement === 'ADMINISTER';
  return (
    <SidebarFormContent
      defaultValues={{}}
      form={
        <PartyCreateForm
          fixedPartyType={fixedPartyType}
          hasAdministerRights={hasAdministerRights}
        />
      }
      formSchema={partyCreateFormSchema}
      header={t`Partij aanmaken`}
      setDrawerClosed={onSidebarClose}
      subtitle={t`Kies een type om vervolgens de andere gegevens in te vullen.`}
      onSubmit={handlePartyCreate}
    />
  );
};

export default PartyCreateSidebarForm;
